import { createContext, useContext } from 'react';

import type { Offer } from '@makemydeal/dr-platform-types';
import type { DealStructureContext } from '@makemydeal/dr-dash-bff-types';

export type DealAssistantStatus = 'idle' | 'loading' | 'success' | 'failure';

export type DealAssistantUnavailableReason = 'loading' | 'calculating' | 'payment-failed' | 'missing-inputs' | 'no-context';

export type DealAssistantContextValue = {
    isOpen: boolean;
    offerSnapshot: Offer | undefined;
    status: DealAssistantStatus;
    isAvailable: boolean;
    unavailableReason: DealAssistantUnavailableReason | undefined;
    dealStructureSnapshot: DealStructureContext | undefined;
    dealStructureSnapshotLabel: string | undefined;
    failureMessage: string | undefined;
    open: () => void;
    close: () => void;
    sendMessage: (text: string) => Promise<void>;
    trackInputInteraction: () => void;
    trackWelcomeLinkClick: (label: string) => void;
    failedMessageIds: readonly string[];
};

export const DealAssistantContext = createContext<DealAssistantContextValue | undefined>(undefined);

export const useDealAssistant = (): DealAssistantContextValue => {
    const value = useContext(DealAssistantContext);
    if (!value) {
        throw new Error('useDealAssistant must be used inside DealAssistantProvider');
    }
    return value;
};

export type DealAssistantAvailability = {
    isAvailable: boolean;
    reason: DealAssistantUnavailableReason | undefined;
    snapshot: DealStructureContext | undefined;
};

export const useDealAssistantAvailability = (): DealAssistantAvailability => {
    const { isAvailable, unavailableReason, dealStructureSnapshot } = useDealAssistant();
    return { isAvailable, reason: unavailableReason, snapshot: dealStructureSnapshot };
};
